import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DE from 'country-flag-icons/react/3x2/DE';
import GB from 'country-flag-icons/react/3x2/GB';
import UA from 'country-flag-icons/react/3x2/UA';
import TR from 'country-flag-icons/react/3x2/TR';
import PL from 'country-flag-icons/react/3x2/PL';
import RO from 'country-flag-icons/react/3x2/RO';
import RU from 'country-flag-icons/react/3x2/RU';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/i18n';

type FlagComponent = (props: { title?: string; style?: React.CSSProperties }) => JSX.Element;

// Nur die tatsächlich genutzten Flaggen importieren (sonst landen ~260 im Bundle).
const FLAGS: Record<string, FlagComponent> = { DE, GB, UA, TR, PL, RO, RU };

function Flag({ country, size = 22 }: { country: string; size?: number }) {
  const Cmp = FLAGS[country];
  if (!Cmp) return null;
  return (
    <Box
      sx={{
        width: size,
        height: Math.round((size * 2) / 3),
        borderRadius: '3px',
        overflow: 'hidden',
        flexShrink: 0,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
        lineHeight: 0,
      }}
    >
      <Cmp title={country} style={{ width: '100%', height: '100%', display: 'block' }} />
    </Box>
  );
}

type Props = {
  /** Farbe des Auslöser-Buttons (z. B. weiß auf dunkler Sidebar). */
  color?: string;
};

/**
 * Sprachwahl über Länderflaggen. Speichert die Auswahl via i18next-Detector
 * in localStorage (immokompass:lang) und wirkt sofort app-weit.
 */
export function LanguageSelector({ color }: Props) {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ??
    SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.language || '').split('-')[0]) ??
    SUPPORTED_LANGUAGES[0];

  const choose = (code: LanguageCode) => {
    void i18n.changeLanguage(code);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={t('lang.select')}>
        <IconButton
          size="small"
          aria-label={t('lang.select')}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color, p: 0.75, borderRadius: '8px' }}
        >
          <Flag country={current.country} size={22} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: -0.5 } } }}
      >
        {SUPPORTED_LANGUAGES.map((l) => {
          const selected = l.code === current.code;
          return (
            <MenuItem key={l.code} selected={selected} onClick={() => choose(l.code)}>
              <ListItemIcon sx={{ minWidth: 34 }}>
                <Flag country={l.country} size={22} />
              </ListItemIcon>
              <ListItemText primary={t(`lang.${l.code}`)} />
              {selected && <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default LanguageSelector;
